// Portfolio Administration v1.0 PR 4a — color band utility shared by
// Insurance / UCC / Due Dates tabs. Implements Convention 8 thresholds:
//   • green:  > 30 days until due
//   • yellow: 15–30 days
//   • orange: 7–14 days
//   • red:    < 7 days OR overdue

export type ColorBand = "green" | "yellow" | "orange" | "red" | "neutral";

/**
 * Whole calendar days until a wall-calendar deadline — the loan module's door
 * onto the shared `calendarDaysUntil`.
 *
 * Every one of this function's callers passes a wall-calendar field (a
 * compliance-calendar `dueDate`, an insurance `expirationDate`, a UCC
 * `lapseDate`, an enforcer requalification date), each traced to a server-side
 * `DateOnly` widening. That is what makes ONE shared rule correct here rather
 * than a per-call-site judgement, and it is why this delegates instead of
 * keeping its own arithmetic.
 *
 * It used to be `Math.round((ms - Date.now()) / 86_400_000)` — an elapsed-time
 * measure, not a calendar one. See `calendarDaysUntil` for why that read
 * `1 day overdue` on the morning a thing was actually due.
 */
export function daysUntil(
  iso: string | null | undefined,
  now?: Date,
): number | null {
  return calendarDaysUntil(iso, now);
}

export function bandForDays(days: number | null): ColorBand {
  if (days === null) return "neutral";
  if (days < 7) return "red";
  if (days < 15) return "orange";
  if (days <= 30) return "yellow";
  return "green";
}

/**
 * The colour band for a wall-calendar deadline, read against the OPERATIONAL
 * calendar day.
 *
 * This used to thread a viewer `tz`. It no longer takes one, and that is the
 * point rather than a simplification: a deadline's day is a fact about the
 * firm, so the band and the label beside it now cannot disagree about what day
 * it is — there is only one day to disagree about. See `OPERATIONAL_TIMEZONE`.
 */
export function bandForDueDate(iso: string | null | undefined): ColorBand {
  return bandForDays(daysUntil(iso));
}

const BAND_BORDER_CLASS: Record<ColorBand, string> = {
  green: "border-l-status-success",
  yellow: "border-l-status-warning",
  orange: "border-l-status-warning",
  red: "border-l-status-error",
  neutral: "border-l-border-light",
};

const BAND_BG_CLASS: Record<ColorBand, string> = {
  green: "bg-status-success/10",
  yellow: "bg-status-warning/10",
  orange: "bg-status-warning/20",
  red: "bg-status-error/10",
  neutral: "bg-white-pure",
};

const BAND_DOT_CLASS: Record<ColorBand, string> = {
  green: "bg-status-success",
  yellow: "bg-status-warning",
  orange: "bg-status-warning",
  red: "bg-status-error",
  neutral: "bg-gray-mid",
};

export function bandBorderClass(band: ColorBand): string {
  return BAND_BORDER_CLASS[band];
}

export function bandBgClass(band: ColorBand): string {
  return BAND_BG_CLASS[band];
}

export function bandDotClass(band: ColorBand): string {
  return BAND_DOT_CLASS[band];
}

export function daysLabel(days: number | null): string {
  if (days === null) return "—";
  if (days === 0) return "Due today";
  if (days > 0) return `${days} day${days === 1 ? "" : "s"} left`;
  const abs = Math.abs(days);
  return `${abs} day${abs === 1 ? "" : "s"} overdue`;
}

import {
  calendarDaysUntil,
  FALLBACK_TIMEZONE,
  formatCalendarDate,
  formatDateInTz,
} from "@/utils/datetime";

export function formatDate(iso: string | null | undefined, tz: string = FALLBACK_TIMEZONE): string {
  if (!iso) return "—";
  return formatDateInTz(iso, tz) || "—";
}

/**
 * A WALL-CALENDAR date (`Apr 25, 2026`), rendered in UTC — the loan module's
 * counterpart to `formatDate`, with the same `—` contract for a null.
 *
 * Use this for a day a human picked or a domain date: a filing date, a due
 * date, a policy's effective or expiration date. Such a value reaches us at UTC
 * MIDNIGHT — the client sends a bare `YYYY-MM-DD`, it binds to a `DateOnly`
 * request field, and the server widens it with
 * `new DateTimeOffset(x.ToDateTime(TimeOnly.MinValue), TimeSpan.Zero)`. Put
 * that through `formatDate`, which pins `America/Phoenix` (UTC-7), and it
 * renders as the PREVIOUS DAY for every user, always.
 *
 * `formatDate` stays correct — and stays the right choice — for genuine
 * instants: `checkedAt`, `preparedAt`, `notifiedAt`, `filedAt`, `certifiedAt`,
 * `termsEditedAt`. Those are clock readings and must keep moving with the
 * viewer's zone.
 *
 * ⚠️ A `Date` suffix does NOT mean wall-calendar and an `At` suffix does NOT
 * mean instant — trace the writer. `CommissionClaim.RevenueReceivedDate` is
 * assigned `candidates.Max(c => c.RecognizedAt)` and is an instant;
 * `BuyerSubmission.FinancialsReceivedAtUtc` is a typed calendar day.
 */
export function calendarDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return formatCalendarDate(iso) || "—";
}
